import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search, User, MapPin,
  Heart, SlidersHorizontal, Map as MapIcon, Music,
  X, Check, Calendar, ArrowUpDown, ChevronDown, Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, query, where, getDocs, onSnapshot, doc, addDoc, deleteDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { NotificationBell } from '../components/NotificationBell';
import { UserMenu } from '../components/UserMenu';
import { Logo } from '../components/Logo';
import { computeSuggestions, FestivalSuggestion, StandProfile } from '../utils/matching';

export interface Festival {
  id: string;
  name: string;
  category: string;
  location: string;
  startDate: string;
  endDate: string;
  status: string;
  description?: string;
  branding?: {
    coverImage: string;
    logoImage?: string;
    description?: string;
  };
  capacity?: number;
  dailyPax?: number;
  expectedPriceRange?: string; // budget | medium | premium | luxury — panier moyen festivalier attendu
  totalStands?: number;
  standDetails?: Record<string, { budget: number; isFree: boolean }>;
  planElements?: { type: string; price?: number }[];
  technical?: {
    pitches: any[];
    equipmentList?: string[];
    hasDrinkingWater?: boolean;
    providesTables?: boolean;
    nightSecurity?: string;
    securityOption?: string;
  };
}

const baseCategories = [
  { name: 'Tous', icon: MapIcon },
  { name: 'Musique', icon: Music },
  { name: 'Gastronomie', icon: MapPin },
  { name: 'Culture', icon: MapPin },
  { name: 'Foire', icon: MapPin },
];

const specialChips = ["Favoris", "Dernière minute", "Gratuit"];
const categoryTabs = ["Tous", "Festival", "Foire", "Marché", "Pop-up", "Salon", "Gastronomie", "Sport", "Locale"];
const dateChips: { label: string; value: 'weekend' | 'semaine' | 'mois' }[] = [
  { label: 'Ce weekend', value: 'weekend' },
  { label: 'Cette semaine', value: 'semaine' },
  { label: 'Ce mois', value: 'mois' },
];

function getMinPrice(festival: Festival): number {
  const details = festival.standDetails;
  if (details) {
    const prices = Object.values(details).filter(d => !d.isFree && d.budget > 0).map(d => d.budget);
    if (prices.length > 0) return Math.min(...prices);
  }
  return Infinity;
}

function getPriceInfo(festival: Festival): { kind: 'from' | 'free' | 'onRequest'; minPrice?: number; isFree: boolean } {
  const details = festival.standDetails;
  if (details && Object.keys(details).length > 0) {
    const values = Object.values(details);
    const hasFree = values.some(d => d.isFree);
    const prices = values.filter(d => !d.isFree && d.budget > 0).map(d => d.budget);
    if (prices.length > 0) return { kind: 'from', minPrice: Math.min(...prices), isFree: false };
    if (hasFree) return { kind: 'free', isFree: true };
  }
  return { kind: 'onRequest', isFree: false };
}

function formatDateFR(dateStr: string, withDay = false): string {
  try {
    const d = new Date(dateStr);
    if (withDay) {
      const day = d.toLocaleDateString('fr-FR', { weekday: 'short' });
      const date = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
      return `${day.charAt(0).toUpperCase() + day.slice(1, -1)}. ${date}`;
    }
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  } catch { return dateStr; }
}

export default function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, userRole } = useAuth();
  const role = userRole || location.state?.role || 'stand';
  // Traduit l'affichage d'une valeur de filtre SANS toucher à la valeur elle-même
  // (utilisée telle quelle dans les comparaisons de filtrage plus bas).
  const labelFor = (v: string) => t(`home.filterValue.${v}`, { defaultValue: v });

  const [activeCategory, setActiveCategory] = useState('Tous');
  const [favorites, setFavorites] = useState<string[]>([]);
  const [festivals, setFestivals] = useState<Festival[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChips, setSelectedChips] = useState<string[]>([]);
  const [dateFilter, setDateFilter] = useState<'weekend' | 'semaine' | 'mois' | ''>('');
  const [monthFilter, setMonthFilter] = useState(''); // format 'YYYY-MM'
  const [regionFilter, setRegionFilter] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'prix' | 'popularite'>('date');
  const [priceMax, setPriceMax] = useState(2000);
  // Critères adossés aux vraies données saisies par l'organisateur
  // (festival.technical.equipmentList, cf. CreateFestivalStep3Page) — retiré
  // Triphasé / Accès Camion : aucun champ ne les portait nulle part, filtres
  // purement décoratifs jusqu'ici (comme priceMax et capacity ci-dessous,
  // jamais appliqués à filteredFestivals avant ce fix du 20/07).
  const [techFilters, setTechFilters] = useState<Record<string, boolean>>({
    'Eau potable': false,
    'Électricité incluse': false,
    'Mobilier fourni': false,
    'Gardiennage nocturne': false,
  });
  const [capacity, setCapacity] = useState<number>(0);
  const [standProfile, setStandProfile] = useState<StandProfile | null>(null);
  const [matches, setMatches] = useState<FestivalSuggestion<Festival>[]>([]);

  const activeFiltersCount =
    selectedChips.length +
    (dateFilter ? 1 : 0) +
    (regionFilter ? 1 : 0) +
    Object.values(techFilters).filter(Boolean).length +
    (capacity > 0 ? 1 : 0) +
    (priceMax < 2000 ? 1 : 0);

  useEffect(() => {
    if (location.state?.activeFilter === 'Favoris') setSelectedChips(['Favoris']);
  }, [location.state]);

  useEffect(() => {
    const fetchPublishedFestivals = async () => {
      try {
        const q = query(collection(db, 'festivals'), where('status', '==', 'published'));
        const snap = await getDocs(q);
        const fests: Festival[] = [];
        snap.forEach(d => fests.push({ id: d.id, ...d.data() } as Festival));
        setFestivals(fests);
      } catch (err) {
        console.error('Failed to fetch festivals', err);
        setFestivals([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPublishedFestivals();
  }, []);

  useEffect(() => {
    if (!user) { setFavorites([]); return; }
    const q = query(collection(db, 'favorites'), where('userId', '==', user.uid));
    const unsub = onSnapshot(q, snap => {
      setFavorites(snap.docs.map(d => d.data().festivalId));
    }, err => console.error('Error fetching favorites:', err));
    return () => unsub();
  }, [user]);

  // Fetch stand profile (stand role only)
  useEffect(() => {
    if (!user || role !== 'stand') return;
    const fetchStand = async () => {
      try {
        const snap = await getDoc(doc(db, 'stands', user.uid));
        if (snap.exists()) setStandProfile(snap.data() as StandProfile);
      } catch (err) {
        console.error('Stand profile fetch error:', err);
      }
    };
    fetchStand();
  }, [user, role]);

  // Suggestions « Pour vous » — pipeline matching (Couche 1 éligibilité +
  // Couche 2 pertinence). Recalculé quand le profil, les festivals ou les
  // favoris (critère d'affinité) changent.
  useEffect(() => {
    if (role !== 'stand' || !standProfile || festivals.length === 0) {
      setMatches([]);
      return;
    }
    setMatches(computeSuggestions(festivals, standProfile, favorites));
  }, [role, standProfile, festivals, favorites]);

  const toggleFavorite = async (e: React.MouseEvent, festivalId: string) => {
    e.stopPropagation();
    if (!user) { alert('Veuillez vous connecter pour ajouter des favoris.'); return; }
    try {
      if (favorites.includes(festivalId)) {
        const q = query(collection(db, 'favorites'), where('userId', '==', user.uid), where('festivalId', '==', festivalId));
        const snap = await getDocs(q);
        for (const fDoc of snap.docs) await deleteDoc(doc(db, 'favorites', fDoc.id));
      } else {
        await addDoc(collection(db, 'favorites'), { userId: user.uid, festivalId, createdAt: serverTimestamp() });
      }
    } catch (err) { console.error('Toggle favorite failed:', err); }
  };

  // Next 12 months for the month picker
  const nextMonths = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + i);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    return { value, label: label.charAt(0).toUpperCase() + label.slice(1) };
  });

  // Régions réellement renseignées par les organisateurs (champ region, valeur
  // parmi les 13 régions françaises — cf. CreateFestivalPage.tsx). On ne retombe
  // plus sur un fragment de `location` (adresse libre) : ça affichait des noms
  // de lieux ("Bateau River's King") au lieu de vraies régions.
  const uniqueCities = Array.from(
    new Set(festivals.map(f => (f as any).region).filter(Boolean))
  ).sort();

  const tabMapping: Record<string, string[]> = {
    Festival: ['music', 'culture', 'convention', 'other'],
    Foire: ['fair'],
    Salon: ['salon', 'fair'],
    'Pop-up': ['market', 'popup'],
    Marché: ['market'],
    Gastronomie: ['food', 'gastronomie'],
    Sport: ['sport'],
    Locale: ['local'],
  };

  const filteredFestivals = festivals
    .filter(festival => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!festival.name?.toLowerCase().includes(q) && !festival.location?.toLowerCase().includes(q)) return false;
      }
      if (activeCategory !== 'Tous') {
        const validCats = tabMapping[activeCategory];
        if (validCats && !validCats.includes(festival.category)) return false;
      }
      if (selectedChips.includes('Favoris') && !favorites.includes(festival.id)) return false;
      if (selectedChips.includes('Pour vous')) {
        const matchIds = matches.map(m => m.festival.id);
        if (!matchIds.includes(festival.id)) return false;
      }
      if (selectedChips.includes('Dernière minute')) {
        try {
          const daysUntil = Math.floor((new Date(festival.startDate).getTime() - Date.now()) / 86400000);
          if (!(daysUntil >= 0 && daysUntil <= 15 && (!festival.totalStands || festival.totalStands === 0))) return false;
        } catch { return false; }
      }
      // "Gratuit" — au moins un type de stand sans droit d'entrée (isFree),
      // même si d'autres types du même festival sont payants.
      if (selectedChips.includes('Gratuit')) {
        const details = festival.standDetails;
        if (!details || !Object.values(details).some(d => d.isFree)) return false;
      }
      if (dateFilter) {
        try {
          const start = new Date(festival.startDate);
          const end = new Date(festival.endDate);
          const now = new Date();
          if (dateFilter === 'weekend') {
            const daysToSat = (6 - now.getDay() + 7) % 7 || 7;
            const sat = new Date(now); sat.setDate(now.getDate() + daysToSat); sat.setHours(0, 0, 0, 0);
            const sun = new Date(sat); sun.setDate(sat.getDate() + 1); sun.setHours(23, 59, 59, 999);
            if (!(end >= sat && start <= sun)) return false;
          }
          if (dateFilter === 'semaine') {
            const weekEnd = new Date(now); weekEnd.setDate(now.getDate() + 7);
            if (!(end >= now && start <= weekEnd)) return false;
          }
          if (dateFilter === 'mois') {
            const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
            if (!(end >= now && start <= monthEnd)) return false;
          }
        } catch { return false; }
      }
      if (monthFilter) {
        try {
          const [y, m] = monthFilter.split('-').map(Number);
          const monthStart = new Date(y, m - 1, 1);
          const monthEnd = new Date(y, m, 0, 23, 59, 59);
          const start = new Date(festival.startDate);
          const end = new Date(festival.endDate);
          if (!(end >= monthStart && start <= monthEnd)) return false;
        } catch { return false; }
      }
      if (regionFilter) {
        const festRegion = (festival as any).region || '';
        const festLocation = festival.location || '';
        if (festRegion !== regionFilter && !festLocation.toLowerCase().includes(regionFilter.toLowerCase())) return false;
      }
      // Prix max — actif seulement si le curseur a été déplacé du plafond (2000€).
      // Un festival dont le prix minimum est indéterminable (getMinPrice = Infinity,
      // aucun standDetails payant renseigné) n'est jamais exclu par ce filtre.
      if (priceMax < 2000) {
        const minPrice = getMinPrice(festival);
        if (Number.isFinite(minPrice) && minPrice > priceMax) return false;
      }
      // Jauge du festival — comparée à dailyPax (nombre de festivaliers), PAS
      // `capacity` qui stocke en réalité le nombre de STANDS (champ mal nommé,
      // conservé pour compat côté formData.totalStands). Festival sans jauge
      // renseignée exclu si le filtre est actif (on ne peut pas confirmer le match).
      if (capacity > 0) {
        const pax = Number(festival.dailyPax) || 0;
        if (capacity === 1 && !(pax > 0 && pax < 5000)) return false;
        if (capacity === 2 && !(pax >= 5000 && pax <= 20000)) return false;
        if (capacity === 3 && !(pax > 20000)) return false;
      }
      // Critères techniques — logique ET (toutes les cases cochées doivent matcher).
      const activeTechFilters = Object.entries(techFilters).filter(([, v]) => v).map(([k]) => k);
      if (activeTechFilters.length > 0) {
        const tech = festival.technical || ({} as NonNullable<Festival['technical']>);
        const equipment = tech.equipmentList || [];
        const matchesTech = (key: string): boolean => {
          if (key === 'Eau potable') return equipment.includes('Eau potable') || tech.hasDrinkingWater === true;
          if (key === 'Électricité incluse') return equipment.includes('Électricité incluse');
          if (key === 'Mobilier fourni') return tech.providesTables === true || ['Tables fournies', 'Chaises fournies', 'Barnum fourni'].some(e => equipment.includes(e));
          if (key === 'Gardiennage nocturne') return tech.securityOption === 'Nocturne inclus' || !!tech.nightSecurity;
          return false;
        };
        if (!activeTechFilters.every(matchesTech)) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'date') return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
      if (sortBy === 'prix') return getMinPrice(a) - getMinPrice(b);
      if (sortBy === 'popularite') return (b.totalStands || 0) - (a.totalStands || 0);
      return 0;
    });

  const hasActiveFilters = activeCategory !== 'Tous' || selectedChips.length > 0 || searchQuery.length > 0 || !!dateFilter || !!monthFilter || !!regionFilter;

  const resetFilters = () => {
    setActiveCategory('Tous');
    setSelectedChips([]);
    setSearchQuery('');
    setDateFilter('');
    setMonthFilter('');
    setRegionFilter('');
    setPriceMax(2000);
    setCapacity(0);
    setTechFilters(prev => Object.fromEntries(Object.keys(prev).map(k => [k, false])));
  };

  // 2 prochains festivals en vedette (startDate >= today, pas dans filteredFestivals pour ne pas dépendre des filtres)
  const featuredFestivals = [...festivals]
    .filter(f => {
      try { return new Date(f.endDate) >= new Date(); } catch { return false; }
    })
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-black text-white font-sans transition-colors duration-200">

      {/* Top bar */}
      <div className="max-w-7xl mx-auto px-6 pt-8 pb-4">

        {/* Search bar */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('home.searchPlaceholder') as string}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-2xl py-3 pl-12 pr-4 text-white focus:outline-none focus:border-[#FF5C00] focus:ring-1 focus:ring-[#FF5C00] transition-all placeholder:text-gray-500"
            />
          </div>

          {/* Sort dropdown */}
          <div className="relative hidden md:block shrink-0">
            <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as typeof sortBy)}
              className="appearance-none bg-white/5 border border-white/10 rounded-2xl pl-9 pr-8 py-3 text-sm text-gray-300 focus:outline-none focus:border-[#FF5C00] cursor-pointer"
            >
              <option value="date">{t('home.sortDate')}</option>
              <option value="prix">{t('home.sortPrice')}</option>
              <option value="popularite">{t('home.sortPopularity')}</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Date chips + Region */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 no-scrollbar mb-4">
          {dateChips.map(({ label, value }) => (
            <button
              key={value}
              onClick={() => { setDateFilter(dateFilter === value ? '' : value); setMonthFilter(''); }}
              className={`flex items-center gap-1.5 whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition-all border shrink-0 ${
                dateFilter === value
                  ? 'bg-[#FF5C00] text-white border-[#FF5C00] shadow-[0_0_15px_rgba(255,92,0,0.35)]'
                  : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10 hover:text-white'
              }`}
            >
              <Calendar className="w-3.5 h-3.5 shrink-0" />
              {labelFor(label)}
            </button>
          ))}

          {/* Month picker */}
          <div className="relative shrink-0">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <select
              value={monthFilter}
              onChange={e => { setMonthFilter(e.target.value); setDateFilter(''); }}
              className={`appearance-none pl-8 pr-7 py-2 rounded-full text-sm font-semibold transition-all border cursor-pointer focus:outline-none ${
                monthFilter
                  ? 'bg-[#FF5C00] text-white border-[#FF5C00] shadow-[0_0_15px_rgba(255,92,0,0.35)]'
                  : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10 hover:text-white'
              }`}
            >
              <option value="">{t('home.month')}</option>
              {nextMonths.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          </div>

          {/* Region select */}
          {uniqueCities.length > 0 && (
            <div className="relative shrink-0">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
              <select
                value={regionFilter}
                onChange={e => setRegionFilter(e.target.value)}
                className={`appearance-none pl-8 pr-7 py-2 rounded-full text-sm font-semibold transition-all border cursor-pointer focus:outline-none ${
                  regionFilter
                    ? 'bg-[#FF5C00] text-white border-[#FF5C00]'
                    : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10 hover:text-white'
                }`}
              >
                <option value="">{t('home.allFrance')}</option>
                {uniqueCities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            </div>
          )}

          {/* Mobile sort */}
          <div className="relative shrink-0 md:hidden">
            <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as typeof sortBy)}
              className="appearance-none bg-white/5 border border-white/10 rounded-full pl-8 pr-7 py-2 text-sm text-gray-400 focus:outline-none cursor-pointer"
            >
              <option value="date">{t('home.sortDateShort')}</option>
              <option value="prix">{t('home.sortPriceShort')}</option>
              <option value="popularite">{t('home.sortPopularityShort')}</option>
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex space-x-1 overflow-x-auto pb-2 no-scrollbar mb-3">
          {categoryTabs.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveCategory(tab)}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
                activeCategory === tab
                  ? 'bg-white text-black border-white'
                  : 'bg-transparent text-gray-400 border-transparent hover:text-white'
              }`}
            >
              {labelFor(tab)}
            </button>
          ))}
        </div>

        {/* Special chips + Filtres button */}
        <div className="flex items-center justify-between">
          <div className="flex space-x-2 overflow-x-auto pb-1 no-scrollbar">
            {specialChips.map(chip => {
              const isSelected = selectedChips.includes(chip);
              return (
                <button
                  key={chip}
                  onClick={() => setSelectedChips(prev =>
                    isSelected ? prev.filter(c => c !== chip) : [...prev, chip]
                  )}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition-all border flex items-center gap-2 ${
                    isSelected
                      ? 'bg-[#FF5C00] text-white border-[#FF5C00] shadow-[0_0_15px_rgba(255,92,0,0.4)]'
                      : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {chip === 'Favoris'
                    ? <Heart className={`w-4 h-4 ${isSelected ? 'fill-current' : ''}`} />
                    : labelFor(chip)}
                </button>
              );
            })}

            {/* Chip "Pour vous" — uniquement pour les exposants avec des matches */}
            {role === 'stand' && matches.length > 0 && (() => {
              const isSelected = selectedChips.includes('Pour vous');
              return (
                <button
                  onClick={() => setSelectedChips(prev =>
                    isSelected ? prev.filter(c => c !== 'Pour vous') : [...prev, 'Pour vous']
                  )}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition-all border flex items-center gap-2 ${
                    isSelected
                      ? 'bg-[#FF5C00] text-white border-[#FF5C00] shadow-[0_0_15px_rgba(255,92,0,0.4)]'
                      : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Sparkles className="w-4 h-4" />
                  {t('home.forYou')}
                </button>
              );
            })()}
          </div>

          <button
            onClick={() => setIsFilterSidebarOpen(true)}
            className="relative hidden md:flex items-center space-x-2 border border-white/10 bg-white/5 hover:bg-white/10 px-5 py-2.5 rounded-full text-sm font-bold text-white hover:border-[#FF5C00]/50 transition-colors ml-4 shrink-0"
          >
            <SlidersHorizontal className="w-4 h-4" />
            <span>{t('home.filters')}</span>
            {activeFiltersCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#FF5C00] text-black w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-black border-2 border-[#050505]">
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>

        {/* Results count */}
        {!loading && (
          <p className="mt-3 text-xs text-gray-500">
            {t('home.resultsCount', { count: filteredFestivals.length })}
          </p>
        )}
      </div>

      {/* En vedette */}
      {!loading && featuredFestivals.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 mb-8">
          <h2 className="text-xs font-black uppercase tracking-widest text-[#FF5C00] mb-4">✦ {t('home.featured')}</h2>
          <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
            {featuredFestivals.map(festival => {
              const { isFree } = getPriceInfo(festival);
              return (
                <div
                  key={festival.id}
                  className="relative shrink-0 w-72 sm:w-80 rounded-2xl overflow-hidden cursor-pointer group"
                  onClick={() => navigate(`/festival/${festival.id}`)}
                >
                  <div className="relative h-44">
                    <img
                      src={festival.branding?.coverImage || 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&q=80'}
                      alt={festival.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    {festival.totalStands === 0 ? (
                      <span className="absolute top-3 left-3 bg-red-600/90 border border-red-500/50 text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full">
                        {t('home.full')}
                      </span>
                    ) : isFree ? (
                      <span className="absolute top-3 left-3 bg-emerald-500 text-black text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full">
                        {t('home.free')}
                      </span>
                    ) : null}
                    <div className="absolute bottom-3 left-3 right-3">
                      <p className="font-black text-white text-base leading-tight truncate">{festival.name}</p>
                      <p className="text-gray-300 text-xs mt-0.5">{festival.location}</p>
                      <p className="text-[#FF5C00] text-xs font-bold mt-1">
                        {formatDateFR(festival.startDate, true)} → {formatDateFR(festival.endDate, true)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Grid */}
      <main className="max-w-7xl mx-auto px-6 py-6 pb-24">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="w-8 h-8 border-4 border-[#FF5C00]/20 border-t-[#FF5C00] rounded-full animate-spin" />
          </div>
        ) : filteredFestivals.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 mb-4">
              {hasActiveFilters ? t('home.noFestivalsFiltered') : t('home.noFestivalsAvailable')}
            </p>
            {hasActiveFilters && (
              <button onClick={resetFilters} className="px-6 py-3 bg-[#FF5C00] text-black font-bold rounded-xl">
                {t('home.resetFilters')}
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 xl:gap-8">
            {filteredFestivals.map(festival => (
              <div
                key={festival.id}
                className="group cursor-pointer flex flex-col"
                onClick={() => navigate(`/festival/${festival.id}`)}
              >
                <div className="relative aspect-[4/3] overflow-hidden rounded-2xl mb-3 bg-[#121212]">
                  <img
                    src={festival.branding?.coverImage || 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&q=80'}
                    alt={festival.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <button
                    className={`absolute top-3 right-3 p-2 transition-all ${
                      favorites.includes(festival.id) ? 'text-[#ff5a00] hover:scale-110' : 'text-white/70 hover:text-white hover:scale-110'
                    }`}
                    onClick={e => toggleFavorite(e, festival.id)}
                  >
                    <Heart className={`w-6 h-6 ${favorites.includes(festival.id) ? 'fill-current' : ''}`} />
                  </button>
                  {festival.totalStands === 0 ? (
                    <div className="absolute top-3 left-3 bg-red-600/90 border border-red-500/50 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-white shadow-sm">
                      {t('home.full')}
                    </div>
                  ) : (
                    <div className="absolute top-3 left-3 bg-[#121212]/90 border border-white/10 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-bold text-white shadow-sm">
                      {t('home.standsAvailable', { count: festival.totalStands || festival.capacity || 0 })}
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-start">
                  <h3 className="font-bold text-base truncate pr-2 text-white">{festival.name}</h3>
                  <span className="font-semibold text-xs text-white bg-white/10 px-2 py-[2px] rounded-md shrink-0">{festival.category}</span>
                </div>

                <p className="text-sm text-gray-400 mt-1">{festival.location}</p>
                <p className="text-sm text-[#FF5C00] font-semibold">
                  {formatDateFR(festival.startDate, true)} → {formatDateFR(festival.endDate, true)}
                </p>

                <div className="mt-2 text-sm flex items-center gap-2">
                  {(() => {
                    const { kind, minPrice, isFree } = getPriceInfo(festival);
                    return isFree ? (
                      <span className="inline-flex items-center gap-1.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-xs font-bold px-2.5 py-1 rounded-full">
                        {t('home.free')}
                      </span>
                    ) : (
                      <span className="font-semibold text-white text-sm">
                        {kind === 'from' ? t('home.priceFrom', { price: minPrice }) : t('home.priceOnRequest')}
                      </span>
                    );
                  })()}
                </div>

                <button
                  onClick={e => { e.stopPropagation(); navigate(`/festival/${festival.id}`); }}
                  className="mt-4 w-full h-12 bg-white/5 hover:bg-white/10 text-white text-[11px] md:text-sm font-black uppercase tracking-widest rounded-xl transition-colors text-center border border-white/10 active:scale-[0.98]"
                >
                  {t('home.viewDetail')}
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="mt-20 border-t border-white/5 bg-[#0d0e12] py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex flex-col items-center md:items-start gap-4">
            <Logo />
            <p className="text-gray-500 text-sm">{t('home.footerCopyright')}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-8 text-sm font-semibold text-gray-400">
            <span onClick={() => navigate('/how-it-works')} className="hover:text-[#FF5C00] cursor-pointer transition-colors">{t('userMenu.howItWorks')}</span>
            <span onClick={() => navigate('/about')} className="hover:text-white cursor-pointer transition-colors">{t('home.footerAbout')}</span>
            <span onClick={() => navigate('/privacy')} className="hover:text-white cursor-pointer transition-colors">{t('home.footerPrivacy')}</span>
            <span onClick={() => navigate('/terms')} className="hover:text-white cursor-pointer transition-colors">{t('home.footerTerms')}</span>
          </div>
        </div>
      </footer>

      {/* Advanced Filters Sidebar */}
      {isFilterSidebarOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setIsFilterSidebarOpen(false)} />
          <div className="relative w-full max-w-sm sm:max-w-md bg-black/70 backdrop-blur-3xl border-l border-white/10 h-full flex flex-col pt-6 pb-8 px-6 sm:px-8 shadow-[-20px_0_40px_rgba(0,0,0,0.5)] transform transition-transform animate-in slide-in-from-right">

            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-black">{t('home.advancedFilters')}</h2>
              <button onClick={() => setIsFilterSidebarOpen(false)} className="p-2 hover:bg-white/10 rounded-full transition-colors bg-white/5">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto pr-2 space-y-10 no-scrollbar">
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-4">{t('home.maxPrice')}</label>
                <div className="flex items-center gap-4 mb-2">
                  <span className="text-gray-500 font-semibold text-sm">0€</span>
                  <input
                    type="range" min="0" max="2000" step="50"
                    value={priceMax}
                    onChange={e => setPriceMax(Number(e.target.value))}
                    className="w-full accent-[#FF5C00] h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="text-[#FF5C00] font-bold w-16 text-right whitespace-nowrap">{priceMax}€</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-4">{t('home.technicalCriteria')}</label>
                <div className="space-y-4">
                  {Object.entries(techFilters).map(([key, value]) => (
                    <label key={key} className="flex items-center group cursor-pointer bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/20 p-4 rounded-xl transition-all">
                      <div className={`w-5 h-5 rounded flex items-center justify-center border transition-all mr-4 shrink-0 ${value ? 'bg-[#FF5C00] border-[#FF5C00]' : 'border-white/30 bg-black/50'}`}>
                        {value && <Check className="w-3.5 h-3.5 text-black" />}
                      </div>
                      <input type="checkbox" className="hidden" checked={value} onChange={e => setTechFilters(prev => ({ ...prev, [key]: e.target.checked }))} />
                      <span className={`${value ? 'text-white' : 'text-gray-400'} transition-colors font-medium`}>{labelFor(key)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-300 mb-4">{t('home.festivalCapacity')}</label>
                <div className="flex gap-3">
                  {[1, 2, 3].map(level => (
                    <button
                      key={level}
                      onClick={() => setCapacity(capacity === level ? 0 : level)}
                      className={`flex-1 flex flex-col items-center justify-center py-4 border rounded-2xl transition-all ${
                        capacity === level
                          ? 'bg-[#FF5C00]/10 border-[#FF5C00] text-[#FF5C00]'
                          : 'bg-white/5 border-white/10 hover:border-white/30 text-gray-400 hover:text-white'
                      }`}
                    >
                      <div className="flex justify-center flex-wrap gap-0.5 mb-2 w-full px-2">
                        {Array(level).fill(0).map((_, i) => <User key={i} className={`w-4 h-4 ${capacity === level ? 'fill-[#FF5C00]' : 'fill-gray-500/20'}`} />)}
                      </div>
                      <span className="text-[10px] sm:text-xs font-semibold whitespace-nowrap">
                        {level === 1 ? '< 5k' : level === 2 ? '5k – 20k' : '> 20k'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-white/10 mt-6 shrink-0">
              <button
                onClick={() => setIsFilterSidebarOpen(false)}
                className="w-full bg-[#FF5C00] hover:bg-[#eb5500] text-black font-bold py-3.5 rounded-xl transition-all shadow-[0_0_20px_rgba(255,92,0,0.3)]"
              >
                {t('home.showResults')} {activeFiltersCount > 0 ? `(${activeFiltersCount})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
